import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import logoUrl from "../../assets/logo.svg";
import { Button } from "../../components/ui/Button";
import {
  AlertIcon,
  EyeIcon,
  EyeOffIcon,
  LockIcon,
  MailIcon,
} from "../../components/icons/Icons";
import { errorMessage } from "../../lib/api";
import { useAuth } from "./AuthProvider";

/**
 * Credentials created by `npm run db:seed`. Printed on the card so the demo is
 * usable without reading the README; drop this block for a real deployment.
 */
//const DEMO_EMAIL = "rfp@demo.nautilusnext.com";
//const DEMO_PASSWORD = "Rfp@12345";

/**
 * Sign-in screen. The Figma file has no login frame, so this is built entirely
 * from the sampled tokens (brand blue, 8px radii, 32px controls, Inter).
 */
export function LoginPage() {
  const { user, initializing, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Where to go after signing in (set by RequireAuth when it redirected here).
  const from = (location.state as { from?: string } | null)?.from ?? "/rfp";

  // Someone with a live session never needs the form.
  if (!initializing && user) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    setError(null);
    if (!email.trim() || !password) {
      setError("Enter both your email and your password.");
      return;
    }

    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
      navigate(from, { replace: true });
    } catch (cause) {
      // 401 -> "Incorrect email or password.", 403 -> deactivated, 0 -> offline.
      setError(errorMessage(cause));
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">
          <img src={logoUrl} alt="" width={30} height={38} />
          <span className="auth-brand__name">RFP</span>
        </div>

        <h1 className="auth-card__title">Sign in</h1>
        <p className="auth-card__subtitle">
          Use your work email to open the RFP workspace.
        </p>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="field">
            <label className="field__label" htmlFor="login-email">
              Email
            </label>
            <div className="input-with-icon">
              <MailIcon size={16} className="input-with-icon__glyph" />
              <input
                id="login-email"
                className="input"
                type="email"
                name="email"
                autoComplete="username"
                autoFocus
                placeholder="you@company.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={submitting}
              />
            </div>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="login-password">
              Password
            </label>
            <div className="input-with-icon">
              <LockIcon size={16} className="input-with-icon__glyph" />
              <input
                id="login-password"
                className="input input--trailing-action"
                type={showPassword ? "text" : "password"}
                name="password"
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={submitting}
              />
              <button
                type="button"
                className="input-with-icon__action"
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                onClick={() => setShowPassword((value) => !value)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOffIcon size={16} />
                ) : (
                  <EyeIcon size={16} />
                )}
              </button>
            </div>
          </div>

          {error ? (
            <p className="auth-error" role="alert">
              <AlertIcon size={16} />
              <span>{error}</span>
            </p>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            className="auth-submit"
            disabled={submitting}
          >
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
